/**
 * ROWIE — PDP size guide
 *
 * The panel and overlay are portalled to <body> on init: an ancestor of
 * the product content column establishes a stacking context, which
 * would otherwise trap a position:fixed child below the gallery. State
 * lives on <body> for the same reason.
 *
 * Open/close handling is delegated at document level rather than bound
 * to a specific trigger element. Two reasons:
 *
 *   1. The Size & Fit tab renders its own [data-sizeguide-open] link,
 *      and more triggers may follow. Delegation means none of them
 *      need registering.
 *   2. Binding to the trigger inside the root broke on section reload.
 *      Once the panel had been portalled out, re-initialising could no
 *      longer find it inside the root and bailed, leaving a dead
 *      trigger in the theme editor.
 */

(function () {
  const SELECTORS = {
    root: '[data-rowie-sizeguide]',
    trigger: '[data-sizeguide-open]',
    panel: '[data-sizeguide-panel]',
    overlay: '[data-sizeguide-overlay]',
    close: '[data-sizeguide-close]',
    nav: '[data-sizeguide-nav]',
    navtab: '[data-sizeguide-tab]',
    panelcontent: '[data-sizeguide-panelcontent]',
  };

  const OPEN_CLASS = 'rowie-sizeguide-open';
  const TRANSITION_MS = 450;
  const REVEAL_PAD = 20;

  let lastFocused = null;
  let overflowTimer = null;

  function panel() {
    return document.querySelector(SELECTORS.panel);
  }

  function isOpen() {
    return document.body.classList.contains(OPEN_CLASS);
  }

  function setTriggerState(expanded) {
    document.querySelectorAll(SELECTORS.trigger).forEach(function (el) {
      // Only elements that declare the attribute get it updated; the tab
      // link is a button too, and both should report the same state.
      if (el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }
    });
  }

  /** Remeasures chart rails, which cannot be sized while hidden. */
  function refreshCharts() {
    if (window.RowieSizeChart && window.RowieSizeChart.refresh) {
      window.RowieSizeChart.refresh(panel() || document);
    }
  }

  function open() {
    if (isOpen()) return;

    clearTimeout(overflowTimer);
    lastFocused = document.activeElement;

    document.body.classList.add(OPEN_CLASS);
    document.body.style.overflow = 'hidden';
    setTriggerState(true);

    const p = panel();
    const closeBtn = p && p.querySelector(SELECTORS.close);
    if (closeBtn) closeBtn.focus();

    // The panel transitions in from translateX(100%); widths are only
    // measurable once it is visible, so this waits a frame.
    requestAnimationFrame(refreshCharts);
  }

  function close() {
    if (!isOpen()) return;

    document.body.classList.remove(OPEN_CLASS);
    setTriggerState(false);

    // Deferred so the slide-out animation is not interrupted by the
    // scrollbar reappearing mid-transition.
    overflowTimer = setTimeout(function () {
      if (!isOpen()) document.body.style.overflow = '';
    }, TRANSITION_MS);

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
      lastFocused = null;
    }
  }

  /**
   * Scrolls a selected tab clear of the nav's edges.
   *
   * Deliberately not scrollIntoView: that walks up every scrollable
   * ancestor, so it would also scroll the panel body and, on mobile,
   * the page behind it. This only ever touches the nav's scrollLeft.
   */
  function revealTab(tab) {
    const nav = tab.closest(SELECTORS.nav);
    if (!nav) return;

    const navRect = nav.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();

    if (tabRect.right > navRect.right) {
      nav.scrollLeft += tabRect.right - navRect.right + REVEAL_PAD;
    } else if (tabRect.left < navRect.left) {
      nav.scrollLeft -= navRect.left - tabRect.left + REVEAL_PAD;
    }
  }

  function selectTab(name) {
    const p = panel();
    if (!p) return;

    p.querySelectorAll(SELECTORS.navtab).forEach(function (tab) {
      const active = tab.getAttribute('data-sizeguide-tab') === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active) revealTab(tab);
    });

    p.querySelectorAll(SELECTORS.panelcontent).forEach(function (content) {
      const active = content.getAttribute('data-sizeguide-panelcontent') === name;
      content.classList.toggle('is-active', active);
      if (active) content.removeAttribute('hidden');
      else content.setAttribute('hidden', '');
    });

    // The revealed panel's chart was unmeasurable until this moment.
    refreshCharts();
  }

  /**
   * Moves the panel and overlay to <body>, clearing any left behind by
   * a previous render. Without the cleanup, every section reload in the
   * theme editor would leave another orphaned panel attached to <body>.
   */
  function portal(root) {
    const nextPanel = root.querySelector(SELECTORS.panel);
    const nextOverlay = root.querySelector(SELECTORS.overlay);

    if (nextPanel) {
      document.querySelectorAll(SELECTORS.panel).forEach(function (el) {
        if (el !== nextPanel && el.parentElement === document.body) el.remove();
      });
      document.body.appendChild(nextPanel);
    }

    if (nextOverlay) {
      document.querySelectorAll(SELECTORS.overlay).forEach(function (el) {
        if (el !== nextOverlay && el.parentElement === document.body) el.remove();
      });
      document.body.appendChild(nextOverlay);
    }
  }

  function initAll() {
    document.querySelectorAll(SELECTORS.root).forEach(function (root) {
      portal(root);
    });
  }

  // --- Delegated handlers, bound once for the life of the page ---

  document.addEventListener('click', function (event) {
    if (event.target.closest(SELECTORS.trigger)) {
      event.preventDefault();
      // Toggle rather than open, so the inline trigger still closes it.
      if (isOpen()) close();
      else open();
      return;
    }

    if (event.target.closest(SELECTORS.close)) {
      event.preventDefault();
      close();
      return;
    }

    if (event.target.closest(SELECTORS.overlay)) {
      close();
      return;
    }

    const tab = event.target.closest(SELECTORS.navtab);
    if (tab) {
      event.preventDefault();
      selectTab(tab.getAttribute('data-sizeguide-tab'));
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isOpen()) close();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function () {
    // Close first — a reload while open would leave the body locked
    // with no visible panel.
    document.body.classList.remove(OPEN_CLASS);
    document.body.style.overflow = '';
    initAll();
  });

  window.RowieSizeGuide = { open: open, close: close, selectTab: selectTab };
})();
